import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAdminApi } from '@/hooks/useAdminApi';
import {
  AlertCircle,
  CheckCircle,
  Globe,
  Loader2,
  Lock,
  LockOpen,
  MapPin,
  Pencil,
  Plus,
  Trash2,
  UserMinus,
  UserPlus,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

interface Territory {
  id: string;
  name: string;
  country_code: string | null;
  region: string | null;
  status: 'locked' | 'unlocked' | 'assigned';
  unlocked_at: string | null;
  assigned_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  agent_id: string | null;
  agent_name: string | null;
  agent_email: string | null;
  affiliate_count: number;
}

interface Agent {
  id: string;
  name: string;
  email: string;
  territory_name: string | null;
}

interface TerritoryStats {
  total: number;
  locked: number;
  unlocked: number;
  assigned: number;
}

export default function AdminTerritories() {
  const adminApi = useAdminApi();

  // Data state
  const [territories, setTerritories] = useState<Territory[]>([]);
  const [stats, setStats] = useState<TerritoryStats>({
    total: 0,
    locked: 0,
    unlocked: 0,
    assigned: 0,
  });
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'assign'>('create');
  const [editingTerritory, setEditingTerritory] = useState<Territory | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [region, setRegion] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState('');

  // Status filter
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'locked', label: 'Locked' },
    { value: 'unlocked', label: 'Unlocked' },
    { value: 'assigned', label: 'Assigned' },
  ];

  const regionOptions = [
    { value: '', label: 'Select Region' },
    { value: 'Europe', label: 'Europe' },
    { value: 'North America', label: 'North America' },
    { value: 'South America', label: 'South America' },
    { value: 'Asia', label: 'Asia' },
    { value: 'Africa', label: 'Africa' },
    { value: 'Oceania', label: 'Oceania' },
    { value: 'Middle East', label: 'Middle East' },
  ];

  // ISO 3166-1 alpha-2 country codes with names
  const countryOptions = [
    { value: '', label: 'Select Country' },
    { value: 'AF', label: 'AF - Afghanistan' },
    { value: 'AL', label: 'AL - Albania' },
    { value: 'DZ', label: 'DZ - Algeria' },
    { value: 'AD', label: 'AD - Andorra' },
    { value: 'AO', label: 'AO - Angola' },
    { value: 'AG', label: 'AG - Antigua and Barbuda' },
    { value: 'AR', label: 'AR - Argentina' },
    { value: 'AM', label: 'AM - Armenia' },
    { value: 'AU', label: 'AU - Australia' },
    { value: 'AT', label: 'AT - Austria' },
    { value: 'AZ', label: 'AZ - Azerbaijan' },
    { value: 'BS', label: 'BS - Bahamas' },
    { value: 'BH', label: 'BH - Bahrain' },
    { value: 'BD', label: 'BD - Bangladesh' },
    { value: 'BB', label: 'BB - Barbados' },
    { value: 'BY', label: 'BY - Belarus' },
    { value: 'BE', label: 'BE - Belgium' },
    { value: 'BZ', label: 'BZ - Belize' },
    { value: 'BJ', label: 'BJ - Benin' },
    { value: 'BT', label: 'BT - Bhutan' },
    { value: 'BO', label: 'BO - Bolivia' },
    { value: 'BA', label: 'BA - Bosnia and Herzegovina' },
    { value: 'BW', label: 'BW - Botswana' },
    { value: 'BR', label: 'BR - Brazil' },
    { value: 'BN', label: 'BN - Brunei' },
    { value: 'BG', label: 'BG - Bulgaria' },
    { value: 'BF', label: 'BF - Burkina Faso' },
    { value: 'BI', label: 'BI - Burundi' },
    { value: 'CV', label: 'CV - Cabo Verde' },
    { value: 'KH', label: 'KH - Cambodia' },
    { value: 'CM', label: 'CM - Cameroon' },
    { value: 'CA', label: 'CA - Canada' },
    { value: 'CF', label: 'CF - Central African Republic' },
    { value: 'TD', label: 'TD - Chad' },
    { value: 'CL', label: 'CL - Chile' },
    { value: 'CN', label: 'CN - China' },
    { value: 'CO', label: 'CO - Colombia' },
    { value: 'KM', label: 'KM - Comoros' },
    { value: 'CG', label: 'CG - Congo' },
    { value: 'CD', label: 'CD - Congo (DRC)' },
    { value: 'CR', label: 'CR - Costa Rica' },
    { value: 'CI', label: "CI - Côte d'Ivoire" },
    { value: 'HR', label: 'HR - Croatia' },
    { value: 'CU', label: 'CU - Cuba' },
    { value: 'CY', label: 'CY - Cyprus' },
    { value: 'CZ', label: 'CZ - Czech Republic' },
    { value: 'DK', label: 'DK - Denmark' },
    { value: 'DJ', label: 'DJ - Djibouti' },
    { value: 'DM', label: 'DM - Dominica' },
    { value: 'DO', label: 'DO - Dominican Republic' },
    { value: 'EC', label: 'EC - Ecuador' },
    { value: 'EG', label: 'EG - Egypt' },
    { value: 'SV', label: 'SV - El Salvador' },
    { value: 'GQ', label: 'GQ - Equatorial Guinea' },
    { value: 'ER', label: 'ER - Eritrea' },
    { value: 'EE', label: 'EE - Estonia' },
    { value: 'SZ', label: 'SZ - Eswatini' },
    { value: 'ET', label: 'ET - Ethiopia' },
    { value: 'FJ', label: 'FJ - Fiji' },
    { value: 'FI', label: 'FI - Finland' },
    { value: 'FR', label: 'FR - France' },
    { value: 'GA', label: 'GA - Gabon' },
    { value: 'GM', label: 'GM - Gambia' },
    { value: 'GE', label: 'GE - Georgia' },
    { value: 'DE', label: 'DE - Germany' },
    { value: 'GH', label: 'GH - Ghana' },
    { value: 'GR', label: 'GR - Greece' },
    { value: 'GD', label: 'GD - Grenada' },
    { value: 'GT', label: 'GT - Guatemala' },
    { value: 'GN', label: 'GN - Guinea' },
    { value: 'GW', label: 'GW - Guinea-Bissau' },
    { value: 'GY', label: 'GY - Guyana' },
    { value: 'HT', label: 'HT - Haiti' },
    { value: 'HN', label: 'HN - Honduras' },
    { value: 'HU', label: 'HU - Hungary' },
    { value: 'IS', label: 'IS - Iceland' },
    { value: 'IN', label: 'IN - India' },
    { value: 'ID', label: 'ID - Indonesia' },
    { value: 'IR', label: 'IR - Iran' },
    { value: 'IQ', label: 'IQ - Iraq' },
    { value: 'IE', label: 'IE - Ireland' },
    { value: 'IL', label: 'IL - Israel' },
    { value: 'IT', label: 'IT - Italy' },
    { value: 'JM', label: 'JM - Jamaica' },
    { value: 'JP', label: 'JP - Japan' },
    { value: 'JO', label: 'JO - Jordan' },
    { value: 'KZ', label: 'KZ - Kazakhstan' },
    { value: 'KE', label: 'KE - Kenya' },
    { value: 'KI', label: 'KI - Kiribati' },
    { value: 'KP', label: 'KP - Korea (North)' },
    { value: 'KR', label: 'KR - Korea (South)' },
    { value: 'KW', label: 'KW - Kuwait' },
    { value: 'KG', label: 'KG - Kyrgyzstan' },
    { value: 'LA', label: 'LA - Laos' },
    { value: 'LV', label: 'LV - Latvia' },
    { value: 'LB', label: 'LB - Lebanon' },
    { value: 'LS', label: 'LS - Lesotho' },
    { value: 'LR', label: 'LR - Liberia' },
    { value: 'LY', label: 'LY - Libya' },
    { value: 'LI', label: 'LI - Liechtenstein' },
    { value: 'LT', label: 'LT - Lithuania' },
    { value: 'LU', label: 'LU - Luxembourg' },
    { value: 'MG', label: 'MG - Madagascar' },
    { value: 'MW', label: 'MW - Malawi' },
    { value: 'MY', label: 'MY - Malaysia' },
    { value: 'MV', label: 'MV - Maldives' },
    { value: 'ML', label: 'ML - Mali' },
    { value: 'MT', label: 'MT - Malta' },
    { value: 'MH', label: 'MH - Marshall Islands' },
    { value: 'MR', label: 'MR - Mauritania' },
    { value: 'MU', label: 'MU - Mauritius' },
    { value: 'MX', label: 'MX - Mexico' },
    { value: 'FM', label: 'FM - Micronesia' },
    { value: 'MD', label: 'MD - Moldova' },
    { value: 'MC', label: 'MC - Monaco' },
    { value: 'MN', label: 'MN - Mongolia' },
    { value: 'ME', label: 'ME - Montenegro' },
    { value: 'MA', label: 'MA - Morocco' },
    { value: 'MZ', label: 'MZ - Mozambique' },
    { value: 'MM', label: 'MM - Myanmar' },
    { value: 'NA', label: 'NA - Namibia' },
    { value: 'NR', label: 'NR - Nauru' },
    { value: 'NP', label: 'NP - Nepal' },
    { value: 'NL', label: 'NL - Netherlands' },
    { value: 'NZ', label: 'NZ - New Zealand' },
    { value: 'NI', label: 'NI - Nicaragua' },
    { value: 'NE', label: 'NE - Niger' },
    { value: 'NG', label: 'NG - Nigeria' },
    { value: 'MK', label: 'MK - North Macedonia' },
    { value: 'NO', label: 'NO - Norway' },
    { value: 'OM', label: 'OM - Oman' },
    { value: 'PK', label: 'PK - Pakistan' },
    { value: 'PW', label: 'PW - Palau' },
    { value: 'PS', label: 'PS - Palestine' },
    { value: 'PA', label: 'PA - Panama' },
    { value: 'PG', label: 'PG - Papua New Guinea' },
    { value: 'PY', label: 'PY - Paraguay' },
    { value: 'PE', label: 'PE - Peru' },
    { value: 'PH', label: 'PH - Philippines' },
    { value: 'PL', label: 'PL - Poland' },
    { value: 'PT', label: 'PT - Portugal' },
    { value: 'QA', label: 'QA - Qatar' },
    { value: 'RO', label: 'RO - Romania' },
    { value: 'RU', label: 'RU - Russia' },
    { value: 'RW', label: 'RW - Rwanda' },
    { value: 'KN', label: 'KN - Saint Kitts and Nevis' },
    { value: 'LC', label: 'LC - Saint Lucia' },
    { value: 'VC', label: 'VC - Saint Vincent and the Grenadines' },
    { value: 'WS', label: 'WS - Samoa' },
    { value: 'SM', label: 'SM - San Marino' },
    { value: 'ST', label: 'ST - São Tomé and Príncipe' },
    { value: 'SA', label: 'SA - Saudi Arabia' },
    { value: 'SN', label: 'SN - Senegal' },
    { value: 'RS', label: 'RS - Serbia' },
    { value: 'SC', label: 'SC - Seychelles' },
    { value: 'SL', label: 'SL - Sierra Leone' },
    { value: 'SG', label: 'SG - Singapore' },
    { value: 'SK', label: 'SK - Slovakia' },
    { value: 'SI', label: 'SI - Slovenia' },
    { value: 'SB', label: 'SB - Solomon Islands' },
    { value: 'SO', label: 'SO - Somalia' },
    { value: 'ZA', label: 'ZA - South Africa' },
    { value: 'SS', label: 'SS - South Sudan' },
    { value: 'ES', label: 'ES - Spain' },
    { value: 'LK', label: 'LK - Sri Lanka' },
    { value: 'SD', label: 'SD - Sudan' },
    { value: 'SR', label: 'SR - Suriname' },
    { value: 'SE', label: 'SE - Sweden' },
    { value: 'CH', label: 'CH - Switzerland' },
    { value: 'SY', label: 'SY - Syria' },
    { value: 'TW', label: 'TW - Taiwan' },
    { value: 'TJ', label: 'TJ - Tajikistan' },
    { value: 'TZ', label: 'TZ - Tanzania' },
    { value: 'TH', label: 'TH - Thailand' },
    { value: 'TL', label: 'TL - Timor-Leste' },
    { value: 'TG', label: 'TG - Togo' },
    { value: 'TO', label: 'TO - Tonga' },
    { value: 'TT', label: 'TT - Trinidad and Tobago' },
    { value: 'TN', label: 'TN - Tunisia' },
    { value: 'TR', label: 'TR - Turkey' },
    { value: 'TM', label: 'TM - Turkmenistan' },
    { value: 'TV', label: 'TV - Tuvalu' },
    { value: 'UG', label: 'UG - Uganda' },
    { value: 'UA', label: 'UA - Ukraine' },
    { value: 'AE', label: 'AE - United Arab Emirates' },
    { value: 'GB', label: 'GB - United Kingdom' },
    { value: 'US', label: 'US - United States' },
    { value: 'UY', label: 'UY - Uruguay' },
    { value: 'UZ', label: 'UZ - Uzbekistan' },
    { value: 'VU', label: 'VU - Vanuatu' },
    { value: 'VA', label: 'VA - Vatican City' },
    { value: 'VE', label: 'VE - Venezuela' },
    { value: 'VN', label: 'VN - Vietnam' },
    { value: 'YE', label: 'YE - Yemen' },
    { value: 'ZM', label: 'ZM - Zambia' },
    { value: 'ZW', label: 'ZW - Zimbabwe' },
  ];

  // Fetch territories
  const fetchTerritories = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await adminApi.getTerritories();

      if (response.success && response.data) {
        setTerritories(response.data.territories);
        setStats(response.data.stats);
      } else {
        setError(response.error || 'Failed to load territories');
      }
    } catch (err) {
      console.error('Failed to fetch territories:', err);
      setError('Failed to load territories');
      toast.error('Failed to load territories');
    } finally {
      setIsLoading(false);
    }
  }, [adminApi]);

  // Fetch agents without territories (for assignment)
  const fetchAvailableAgents = useCallback(async () => {
    try {
      const response = await adminApi.getAgents();
      if (response.success && response.data) {
        // Filter to agents without territories
        setAgents(
          response.data.agents
            .filter((a) => !a.territory_name)
            .map((a) => ({
              id: a.id,
              name: a.name,
              email: a.email,
              territory_name: a.territory_name,
            }))
        );
      }
    } catch (err) {
      console.error('Failed to fetch agents:', err);
    }
  }, [adminApi]);

  useEffect(() => {
    fetchTerritories();
    fetchAvailableAgents();
  }, [fetchTerritories, fetchAvailableAgents]);

  // Reset form
  const resetForm = () => {
    setName('');
    setCountryCode('');
    setRegion('');
    setNotes('');
    setSelectedAgentId('');
    setEditingTerritory(null);
    setModalMode('create');
  };

  // Open modal for new territory
  const handleAddTerritory = () => {
    resetForm();
    setModalMode('create');
    setShowModal(true);
  };

  // Open modal for editing
  const handleEditTerritory = (territory: Territory) => {
    setEditingTerritory(territory);
    setModalMode('edit');
    setName(territory.name);
    setCountryCode(territory.country_code || '');
    setRegion(territory.region || '');
    setNotes(territory.notes || '');
    setShowModal(true);
  };

  // Open modal for assignment
  const handleOpenAssignModal = (territory: Territory) => {
    setEditingTerritory(territory);
    setModalMode('assign');
    setSelectedAgentId('');
    fetchAvailableAgents();
    setShowModal(true);
  };

  // Save territory (create or update)
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('Please enter a territory name');
      return;
    }

    setIsSaving(true);

    try {
      if (modalMode === 'edit' && editingTerritory) {
        const response = await adminApi.updateTerritory(editingTerritory.id, {
          name: name.trim(),
          country_code: countryCode.trim() || undefined,
          region: region || undefined,
          notes: notes.trim() || undefined,
        });

        if (response.success) {
          toast.success('Territory updated successfully');
          setShowModal(false);
          resetForm();
          fetchTerritories();
        } else {
          toast.error(response.error || 'Failed to update territory');
        }
      } else if (modalMode === 'assign' && editingTerritory) {
        if (!selectedAgentId) {
          toast.error('Please select an agent');
          setIsSaving(false);
          return;
        }

        const response = await adminApi.assignTerritory(editingTerritory.id, selectedAgentId);

        if (response.success) {
          toast.success('Territory assigned successfully');
          setShowModal(false);
          resetForm();
          fetchTerritories();
          fetchAvailableAgents();
        } else {
          toast.error(response.error || 'Failed to assign territory');
        }
      } else {
        const response = await adminApi.createTerritory({
          name: name.trim(),
          country_code: countryCode.trim() || undefined,
          region: region || undefined,
          notes: notes.trim() || undefined,
        });

        if (response.success) {
          toast.success('Territory created successfully');
          setShowModal(false);
          resetForm();
          fetchTerritories();
        } else {
          toast.error(response.error || 'Failed to create territory');
        }
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save territory');
    } finally {
      setIsSaving(false);
    }
  };

  // Lock/Unlock territory
  const handleToggleLock = async (territory: Territory) => {
    try {
      if (territory.status === 'locked') {
        const response = await adminApi.unlockTerritory(territory.id);
        if (response.success) {
          toast.success('Territory unlocked');
          fetchTerritories();
        } else {
          toast.error(response.error || 'Failed to unlock territory');
        }
      } else if (territory.status === 'unlocked') {
        const response = await adminApi.lockTerritory(territory.id);
        if (response.success) {
          toast.success('Territory locked');
          fetchTerritories();
        } else {
          toast.error(response.error || 'Failed to lock territory');
        }
      }
    } catch (err) {
      console.error('Toggle lock error:', err);
      toast.error('Failed to update territory');
    }
  };

  // Unassign territory
  const handleUnassign = async (territory: Territory) => {
    if (!confirm(`Unassign ${territory.agent_name} from ${territory.name}?`)) return;

    try {
      const response = await adminApi.unassignTerritory(territory.id);
      if (response.success) {
        toast.success('Territory unassigned');
        fetchTerritories();
        fetchAvailableAgents();
      } else {
        toast.error(response.error || 'Failed to unassign territory');
      }
    } catch (err) {
      console.error('Unassign error:', err);
      toast.error('Failed to unassign territory');
    }
  };

  // Delete territory
  const handleDelete = async (territory: Territory) => {
    if (!confirm(`Delete territory "${territory.name}"? This cannot be undone.`)) return;

    try {
      const response = await adminApi.deleteTerritory(territory.id);
      if (response.success) {
        toast.success('Territory deleted');
        fetchTerritories();
      } else {
        toast.error(response.error || 'Failed to delete territory');
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to delete territory');
    }
  };

  // Filter territories
  const filteredTerritories = territories.filter((t) => {
    if (statusFilter === 'all') return true;
    return t.status === statusFilter;
  });

  // Status badge styling
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'locked':
        return (
          <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/20">
            <Lock className="h-3 w-3 mr-1" />
            Locked
          </Badge>
        );
      case 'unlocked':
        return (
          <Badge
            variant="outline"
            className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20"
          >
            <LockOpen className="h-3 w-3 mr-1" />
            Unlocked
          </Badge>
        );
      case 'assigned':
        return (
          <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">
            <CheckCircle className="h-3 w-3 mr-1" />
            Assigned
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gradient-gold">Territory Management</h1>
          <p className="mt-1 text-muted-foreground">
            Manage global territories and agent assignments.
          </p>
        </div>
        <Button className="gap-2" onClick={handleAddTerritory}>
          <Plus className="h-4 w-4" />
          Add Territory
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4 mb-8">
        <Card className="animate-fade-in">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-primary/10 p-3">
                <Globe className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Territories</p>
                <p className="text-2xl font-bold">{isLoading ? '...' : stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="animate-fade-in" style={{ animationDelay: '50ms' }}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-red-500/10 p-3">
                <Lock className="h-6 w-6 text-red-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Locked</p>
                <p className="text-2xl font-bold">{isLoading ? '...' : stats.locked}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="animate-fade-in" style={{ animationDelay: '100ms' }}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-yellow-500/10 p-3">
                <LockOpen className="h-6 w-6 text-yellow-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Unlocked</p>
                <p className="text-2xl font-bold">{isLoading ? '...' : stats.unlocked}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="animate-fade-in" style={{ animationDelay: '150ms' }}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-green-500/10 p-3">
                <CheckCircle className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Assigned</p>
                <p className="text-2xl font-bold">{isLoading ? '...' : stats.assigned}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Error State */}
      {error && (
        <Card className="mb-6 border-destructive">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <span>{error}</span>
              <Button variant="outline" size="sm" className="ml-auto" onClick={fetchTerritories}>
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter */}
      <div className="mb-6 flex items-center gap-4">
        <div className="w-48">
          <Select
            options={statusOptions}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          />
        </div>
        <span className="text-sm text-muted-foreground">
          Showing {filteredTerritories.length} of {territories.length} territories
        </span>
      </div>

      {/* Territories Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" />
            All Territories
          </CardTitle>
          <CardDescription>Territory status and agent assignments</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredTerritories.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <Globe className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-1">No territories found</h3>
              <p className="text-muted-foreground max-w-md">
                {statusFilter !== 'all'
                  ? `No territories with status "${statusFilter}"`
                  : 'Create your first territory to get started.'}
              </p>
              {statusFilter === 'all' && (
                <Button className="mt-4" onClick={handleAddTerritory}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Territory
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Territory</TableHead>
                  <TableHead>Region</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Agent</TableHead>
                  <TableHead>Affiliates</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTerritories.map((territory, index) => (
                  <TableRow
                    key={territory.id}
                    className="animate-fade-in"
                    style={{ animationDelay: `${index * 30}ms` }}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="rounded-lg bg-primary/10 p-2">
                          <MapPin className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <span className="font-medium">{territory.name}</span>
                          {territory.country_code && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              ({territory.country_code})
                            </span>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-muted-foreground">{territory.region || '-'}</span>
                    </TableCell>
                    <TableCell>{getStatusBadge(territory.status)}</TableCell>
                    <TableCell>
                      {territory.agent_name ? (
                        <div>
                          <span className="font-medium">{territory.agent_name}</span>
                          <span className="block text-xs text-muted-foreground">
                            {territory.agent_email}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{territory.affiliate_count || 0}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditTerritory(territory)}
                          title="Edit"
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>

                        {territory.status === 'locked' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleLock(territory)}
                            title="Unlock"
                            className="text-yellow-500 hover:text-yellow-600"
                          >
                            <LockOpen className="h-3 w-3" />
                          </Button>
                        )}

                        {territory.status === 'unlocked' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleLock(territory)}
                              title="Lock"
                              className="text-red-500 hover:text-red-600"
                            >
                              <Lock className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenAssignModal(territory)}
                              title="Assign Agent"
                              className="text-green-500 hover:text-green-600"
                            >
                              <UserPlus className="h-3 w-3" />
                            </Button>
                          </>
                        )}

                        {territory.status === 'assigned' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUnassign(territory)}
                            title="Unassign"
                            className="text-orange-500 hover:text-orange-600"
                          >
                            <UserMinus className="h-3 w-3" />
                          </Button>
                        )}

                        {territory.status !== 'assigned' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(territory)}
                            title="Delete"
                            className="text-red-500 hover:text-red-600"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit/Assign Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4 animate-fade-in">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  {modalMode === 'create'
                    ? 'Add Territory'
                    : modalMode === 'edit'
                      ? 'Edit Territory'
                      : 'Assign Territory'}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>
                {modalMode === 'create'
                  ? 'Create a new territory'
                  : modalMode === 'edit'
                    ? 'Update territory details'
                    : `Assign agent to ${editingTerritory?.name}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSave} className="space-y-4">
                {modalMode !== 'assign' ? (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="territory-name">Name *</Label>
                      <Input
                        id="territory-name"
                        placeholder="e.g., South Africa, UK - London"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Country</Label>
                        <Select
                          options={countryOptions}
                          value={countryCode}
                          onChange={(e) => setCountryCode(e.target.value)}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Region</Label>
                        <Select
                          options={regionOptions}
                          value={region}
                          onChange={(e) => setRegion(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="notes">Notes</Label>
                      <textarea
                        id="notes"
                        className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        placeholder="Additional notes about this territory..."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                      />
                    </div>
                  </>
                ) : (
                  <div className="space-y-2">
                    <Label>Select Agent</Label>
                    {agents.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No agents available without territories.
                      </p>
                    ) : (
                      <Select
                        options={[
                          { value: '', label: 'Select an agent...' },
                          ...agents.map((a) => ({
                            value: a.id,
                            label: `${a.name} (${a.email})`,
                          })),
                        ]}
                        value={selectedAgentId}
                        onChange={(e) => setSelectedAgentId(e.target.value)}
                      />
                    )}
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    type="button"
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1"
                    type="submit"
                    disabled={isSaving || (modalMode === 'assign' && agents.length === 0)}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : modalMode === 'create' ? (
                      'Create Territory'
                    ) : modalMode === 'edit' ? (
                      'Save Changes'
                    ) : (
                      'Assign Agent'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </DashboardLayout>
  );
}
